import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, Link2, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Overline } from "@/components/Reveal";
import EpisodeCard from "@/components/EpisodeCard";
import TheProgram from "@/components/home/TheProgram";
import { api } from "@/lib/api";

export default function EpisodeDetailPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api
      .get(`/episodes/${slug}`)
      .then(setData)
      .catch(() => setNotFound(true));
  }, [slug]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied. Go spread the word.");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = window.location.href;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      toast.success("Link copied. Go spread the word.");
    }
  };

  if (notFound) {
    return (
      <div data-testid="episode-not-found" className="pt-40 pb-32">
        <div className="mx-auto max-w-3xl px-5 md:px-10">
          <h1 className="font-display text-4xl font-black md:text-6xl">
            That program isn&rsquo;t on the shelf.
          </h1>
          <p className="mt-5 text-latte">
            It may not have dropped yet &mdash; Let&rsquo;s Elaborate Wednesdays,
            The Program Saturdays.
          </p>
          <Link
            to="/episodes"
            data-testid="episode-not-found-back"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 font-bold text-ink transition-colors hover:bg-neon hover:text-cream"
          >
            <ArrowLeft size={16} /> Back to the Archive
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24">
        <p className="font-editorial italic text-2xl text-latte">Cueing the record…</p>
      </div>
    );
  }

  const { episode, related_episodes } = data;
  const date = episode.published_at
    ? format(new Date(episode.published_at), "MMMM d, yyyy")
    : "";

  return (
    <div data-testid="episode-detail" className="pt-32 pb-28 md:pt-40">
      <div className="mx-auto max-w-5xl px-5 md:px-10">
        <Reveal>
          <Link
            to="/episodes"
            data-testid="episode-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-latte transition-colors hover:text-gold"
          >
            <ArrowLeft size={16} /> The Archive
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Overline>
              {episode.show || "The Program"} {episode.episode_number ? String(episode.episode_number).padStart(3, "0") : ""} &mdash; {date}
            </Overline>
            {episode.duration && (
              <span className="rounded-full border border-corduroy px-3 py-1 text-xs text-latte">
                {episode.duration}
              </span>
            )}
          </div>
          <h1 className="mt-5 font-display text-4xl font-black leading-[0.95] tracking-tight md:text-6xl">
            {episode.title}
          </h1>
          {episode.subtitle && <p data-testid="episode-subtitle" className="mt-5 max-w-3xl font-editorial text-2xl italic leading-relaxed text-latte md:text-3xl">{episode.subtitle}</p>}
        </Reveal>

        <Reveal delay={0.1} className="mt-12">
          {episode.thumbnail_url && <img data-testid="episode-thumbnail" src={episode.thumbnail_url} alt={episode.thumbnail_alt || ""} className="mb-8 aspect-video w-full rounded-2xl border border-corduroy object-cover" />}
          <div
            data-testid="episode-video"
            className="overflow-hidden rounded-2xl border border-corduroy bg-cocoa"
          >
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${episode.youtube_id}`}
                title={episode.title}
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05} className="mt-14">
          {episode.description?.split("\n\n").map((p, i) => (
            <p key={i} className="mb-5 max-w-3xl text-base leading-relaxed text-cream/85 md:text-lg">
              {p}
            </p>
          ))}
          {episode.topics?.length > 0 && (
            <div data-testid="episode-topics" className="mt-8 flex flex-wrap gap-2">
              {episode.topics.map((t) => (
                <span key={t} className="rounded-full border border-corduroy bg-cocoa px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-latte">
                  {t}
                </span>
              ))}
            </div>
          )}
          {episode.sources?.length > 0 && <div data-testid="episode-sources" className="mt-10 border-l-2 border-gold pl-5"><Overline>Sources &amp; receipts</Overline><ul className="mt-4 space-y-2">{episode.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener" className="text-sm font-semibold text-gold underline-offset-4 hover:underline">{source.label}</a></li>)}</ul></div>}
        </Reveal>

        {episode.chapters?.length > 0 && (
          <Reveal className="mt-16">
            <TheProgram
              embedded
              segments={episode.chapters.map((c, i) => ({
                n: String(i + 1).padStart(2, "0"),
                title: c.label,
                meta: c.time,
              }))}
              episodeLabel={`Program ${String(episode.episode_number).padStart(3, "0")}`}
              date={episode.published_at}
              intro="The rundown for this edition, in the order it aired."
            />
          </Reveal>
        )}

        {episode.shis_take && (
          <Reveal className="mt-16">
            <div data-testid="episode-shis-take" className="border-l-2 border-gold pl-6 md:pl-10">
              <Overline>Shi&rsquo;s Take</Overline>
              <p className="mt-4 font-editorial text-2xl italic leading-relaxed text-cream md:text-3xl">
                {episode.shis_take}
              </p>
            </div>
          </Reveal>
        )}

        {episode.final_word && (
          <Reveal className="mt-16">
            <div data-testid="episode-final-word" className="rounded-3xl border border-corduroy bg-cocoa p-8 md:p-12">
              <Overline>Final Word</Overline>
              <p className="mt-5 font-display text-2xl font-bold leading-snug text-cream md:text-4xl">
                {episode.final_word}
              </p>
            </div>
          </Reveal>
        )}

        <Reveal className="mt-14">
          <div className="flex flex-wrap items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-latte">
              Share this program
            </span>
            <button
              onClick={copyLink}
              data-testid="episode-share-copy"
              className="inline-flex items-center gap-2 rounded-full border border-corduroy px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:border-gold hover:text-gold"
            >
              <Link2 size={15} /> Copy Link
            </button>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                `"${episode.title}" — That’s What SHi Said with Ashyla`
              )}&url=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : ""
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="episode-share-x"
              className="inline-flex items-center gap-2 rounded-full border border-corduroy px-5 py-2.5 text-sm font-semibold text-cream transition-colors hover:border-gold hover:text-gold"
            >
              <Twitter size={15} /> Post to X
            </a>
          </div>
        </Reveal>
      </div>

      {related_episodes?.length > 0 && (
        <div className="mx-auto mt-24 max-w-7xl px-5 md:px-10">
          {related_episodes?.length > 0 && (
            <>
              <Reveal>
                <h2 className="font-display text-3xl font-black md:text-4xl">More from the Archive</h2>
              </Reveal>
              <div data-testid="related-episodes" className="mt-8 grid gap-6 md:grid-cols-3">
                {related_episodes.map((ep) => (
                  <EpisodeCard key={ep.slug} episode={ep} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
